/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import Grid from "@mui/material/Grid";
import summaryStyle from "../summaryStyle";
import GridEditRow from "../../Common/GridEditRow/GridEditRow";
import Typography from "@mui/material/Typography";
import {Box} from "@mui/material";

type AssetsSummaryProps = {
  formData: Record<string, any>;
  basePath: string;
};

const AssetsSummary = ({ formData, basePath }: AssetsSummaryProps) => {
  return (
    <>
      <Grid item xs={12} sx={summaryStyle.subHeadline}>
        <Typography variant={"h5"}>Vermögen</Typography>
      </Grid>
      <Grid item xs={12} sx={summaryStyle.question}>
        <Box id={"hasBankAccount"} sx={summaryStyle.scrollToAnchor} />
        Bankkonten
      </Grid>
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "konten",
          state: {
            field: "hasBankAccount",
          },
        }}
      >
        {formData.hasBankAccount === "no" ? (
          "Keine Bankkonten"
        ) : (
          <>
            {formData.bankAccounts?.map((bankAccount: Record<string, any>, key: number) => (
              <React.Fragment key={key}>
                {bankAccount.type} von {bankAccount.owner} bei {bankAccount.name}, Guthaben: {bankAccount.amount} €
                <br />
                <br />
              </React.Fragment>
            ))}
          </>
        )}
      </GridEditRow>
      <Grid item xs={12} sx={summaryStyle.question}>
        <Box id={"hasRealEstate"} sx={summaryStyle.scrollToAnchor} />
        Grundeigentum
      </Grid>
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "grundeigentum",
          state: {
            field: "hasRealEstate",
          },
        }}
      >
        {formData.hasRealEstate === "no" ? (
          "Kein Grundeigentum"
        ) : (
          <>
            Größe: {formData.realEstate1Size} m²
            <br />
            {formData.realEstate1NumberApartments === 0 && "Keine Wohneinheiten"}
            {formData.realEstate1NumberApartments === 1 && "Eine Wohneinheit"}
            {formData.realEstate1NumberApartments > 1 && formData.realEstate1NumberApartments + " Wohneinheiten"}
            <br />
            Anschrift/Bezeichnung: {formData.realEstate1Location}
            <br />
            {formData.realEstate1Ownership}
            <br />
            Wert: {formData.realEstate1Value} €<br />
          </>
        )}
      </GridEditRow>

      <Grid item xs={12} sx={summaryStyle.question}>
        <Box id={"hasVehicles"} sx={summaryStyle.scrollToAnchor} />
        Fahrzeuge
      </Grid>
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "kraftfahrzeuge",
          state: {
            field: "hasRealEstate",
          },
        }}
      >
        {formData.hasVehicles === "no" ? (
          "Keine Fahrzeuge"
        ) : (
          <>
            {formData.vehicles?.map((vehicle: Record<string, any>, key: number) => (
              <React.Fragment key={key}>
                {vehicle.make}
                <br />
                Baujahr {vehicle.manufactured}, gekauft {vehicle.purchased}
                <br />
                Fahrleistung {vehicle.mileage} km
                <br />
                {vehicle.ownership}
                <br />
                Wert: {vehicle.value} €<br />
                <br />
              </React.Fragment>
            ))}
          </>
        )}
      </GridEditRow>

      <Grid item xs={12} sx={summaryStyle.question}>
        <Box id={"cashAmount"} sx={summaryStyle.scrollToAnchor} />
        Bargeld oder Wertgegenstände
      </Grid>
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "bargeld-wertgegenstaende",
          state: {
            field: "cashAmount",
          },
        }}
      >
        Bargeld: {formData.cashAmount > 0 && formData.cashAmount + " €"}
        {formData.cashAmount === 0 && "nicht vorhanden"}
        <br />
        {formData.hasValuables === "no" ? (
          "Keine Wertgegenstände"
        ) : (
          <>
            {formData.valuables?.map((valuable: Record<string, any>, key: number) => (
              <React.Fragment key={key}>
                <br />
                {valuable.name}
                <br />
                {valuable.ownership}
                <br />
                Wert: {valuable.value} €<br />
              </React.Fragment>
            ))}
          </>
        )}
      </GridEditRow>

      <Grid item xs={12} sx={summaryStyle.question}>
        <Box id={"hasPensionInsurance"} sx={summaryStyle.scrollToAnchor} />
        Lebens- und Rentenversicherungen
      </Grid>
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "lebensversicherungen",
          state: {
            field: "hasPensionInsurance",
          },
        }}
      >
        {formData.hasPensionInsurance === "no" ? (
          "Keine Lebensversicherungen"
        ) : (
          <>
            {formData.pensionInsurances?.map((pensionInsurance: Record<string, any>, key: number) => (
              <React.Fragment key={key}>
                <br />
                {pensionInsurance.name}
                <br />
                Versicherungsnehmer: {pensionInsurance.policyHolder}
                <br />
                Abschlussdatum: {pensionInsurance.date}
                <br />
                Wert: {pensionInsurance.value} €<br />
              </React.Fragment>
            ))}
          </>
        )}
      </GridEditRow>

      <Grid item xs={12} sx={summaryStyle.question}>
        <Box id={"hasOtherAssets"} sx={summaryStyle.scrollToAnchor} />
        Sonstige Vermögenswerte
      </Grid>
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "sontiges-vermoegen",
          state: {
            field: "hasOtherAssets",
          },
        }}
      >
        {formData.hasOtherAssets === "no" ? (
          "Keine weiteren Vermögenswerte"
        ) : (
          <>
            {formData.otherAssets?.map((otherAsset: Record<string, any>, key: number) => (
              <React.Fragment key={key}>
                <br />
                {otherAsset.name}
                <br />
                {otherAsset.ownership}
                <br />
                Wert: {otherAsset.value} €<br />
              </React.Fragment>
            ))}
          </>
        )}
      </GridEditRow>
    </>
  );
};

export default AssetsSummary;
